import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useRef, useState } from "react";
import Button from "src/components/form/Button";
import Search from "src/components/form/Search";
import TopMenuItemGroup from "src/components/navigation/topNavigation/TopMenuItemGroup";
import TopMenuItemLink from "src/components/navigation/topNavigation/TopMenuItemLink";
import { outerPages } from "src/config/host";
import { searchMenu } from "src/helpers";
import { classNames } from "src/helpers/classNames";
import useClickOutside from "src/hooks/useClickOutside";
import useDevice from "src/hooks/useDevice";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";

const TopNavigationMenu = ({ isMenu, setIsMenu, menus = [], simulatedMenus = [], setSimulatedMenus = () => {}, keyword = "", setKeyword = () => {}, handleMenuClick = () => {} }) => {
  const { group: group_id, page: page_id, form: form_id, document: document_id } = useUrlQueryParams();
  const device = useDevice();
  const [toggleId, setToggleId] = useState(null);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [overflowMenus, setOverflowMenus] = useState([]);

  const menuContainerRef = useRef(null);
  const menuItemsRef = useRef([]);
  const moreButtonRef = useRef(null);
  const moreDropdownRef = useRef(null);

  useClickOutside(
    moreDropdownRef,
    () => {
      if (showMoreDropdown) setShowMoreDropdown(false);
    },
    [moreButtonRef],
  );

  useEffect(() => {
    menuItemsRef.current = menuItemsRef.current.slice(0, simulatedMenus.length);
  }, [simulatedMenus]);

  const detectOverflow = useCallback(() => {
    if (!menuContainerRef.current || simulatedMenus.length === 0) return;

    const containerWidth = menuContainerRef.current.clientWidth;
    const moreButtonWidth = 140;
    let availableWidth = containerWidth - moreButtonWidth;
    let overflowIndex = simulatedMenus.length;

    let totalWidth = 0;
    for (let i = 0; i < menuItemsRef.current.length; i++) {
      const menuItem = menuItemsRef.current[i];
      if (!menuItem) continue;

      const itemWidth = menuItem.getBoundingClientRect().width;
      totalWidth += itemWidth;

      if (totalWidth > availableWidth) {
        overflowIndex = i;
        break;
      }
    }

    if (overflowIndex < simulatedMenus.length && window.innerWidth > 1279) {
      setOverflowMenus(simulatedMenus.slice(overflowIndex));
    } else {
      setOverflowMenus([]);
    }
  }, [simulatedMenus]);

  useEffect(() => {
    // Function to run detection safely after layout updates
    const runDetection = () => {
      requestAnimationFrame(() => {
        detectOverflow();
      });
    };

    // Initial run on mount
    runDetection();

    // Resize handler
    const handleResize = () => {
      runDetection();
    };

    // Listen to window resize
    window.addEventListener("resize", handleResize);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [detectOverflow]);

  const isOuterPage = outerPages.includes(window.location.href);

  const searchOnMenus = (keyword = "") => {
    if (!keyword) {
      setSimulatedMenus(menus);
    } else {
      setSimulatedMenus(searchMenu(menus, keyword));
    }
  };

  const groupMenus = menus.filter((menu) => menu.type === "group");
  const individualMenus = menus.filter((menu) => menu.type !== "group");

  const handleClick = (group_id) => {
    setToggleId(group_id ? group_id : "all");
    handleMenuClick();
  };

  const toggleMoreDropdown = () => {
    setShowMoreDropdown(!showMoreDropdown);
  };

  const hasActiveInOverflow = overflowMenus.some((item) => {
    if (item.type === "group") {
      if (item?.slug === group_id) return true;

      if (Array.isArray(item.submenu)) {
        return item.submenu.some((sub) => sub?.slug === group_id);
      }
    }
    if (item?.slug === page_id) return true;

    if (Array.isArray(item.submenu)) {
      return item.submenu.some((sub) => sub?.slug === page_id);
    }
    return false;
  });

  return (
    <>
      <div
        className={classNames("fixed left-0 top-0 z-[49] block h-full w-screen bg-black transition-all duration-200 xl:z-[39] xl:hidden", isMenu ? "visible opacity-80" : "invisible opacity-0")}
        onClick={() => setIsMenu(!isMenu)}></div>
      <div className={classNames("fixed left-0 top-0 z-50 flex h-screen w-80 flex-col !bg-leftNavColor bg-white shadow transition-all duration-200 xl:sticky xl:top-16 xl:z-40 xl:h-auto xl:w-full", !isMenu || isOuterPage ? "-translate-x-96" : "translate-x-0")}>
        {isMenu && device === "mobile" && (
          <Button
            buttonIcon={XMarkIcon}
            buttonIconPosition={"right"}
            buttonClasses={"!bg-transparent !absolute top-2 left-full !ml-2 !w-10 !h-10 !px-0 !rounded-full !border-2 !border-white"}
            buttonIconClass={"!w-6 !h-6 !stroke-2"}
            buttonFunction={() => setIsMenu(!isMenu)}
          />
        )}
        {/* <div className={classNames("relative flex h-16 flex-shrink-0 items-center !bg-headerColor pl-4 md:hidden md:justify-center md:pl-0")}>
          
          <div className="h-10">
            <Link
              to={"/login"}
              className="block h-full w-full">
              {!workspace_id || workspaceDetails?.use_global_settings ? (
                site?._id && isLogoVisible ? (
                  <Image
                    src={site?.image_logo ? `${site.image_logo}?${new Date().getTime()}` : Logo}
                    alt={"Logo"}
                    width={"100%"}
                    height={"100%"}
                    effect={"blur"}
                    classes={"object-contain object-left"}
                    hideImageOnError
                  />
                ) : (
                  <></>
                )
              ) : workspaceDetails?._id && isLogoVisible ? (
                <Image
                  src={workspaceDetails?.image_logo ? `${workspaceDetails?.image_logo}?${new Date().getTime()}` : Logo}
                  alt={"Logo"}
                  width={"100%"}
                  height={"100%"}
                  effect={"blur"}
                  classes={"object-contain object-left"}
                  hideImageOnError
                />
              ) : (
                <></>
              )}
            </Link>
          </div>
        </div> */}
        {groupMenus.reduce((total, group) => (group?.permissions?.length ? total + group?.permissions?.length : total), individualMenus.length) > 15 && (
          <div className={classNames("block flex-shrink-0 p-2 px-2 py-3 xl:hidden xl:px-0 xl:py-0")}>
            <Search
              value={keyword}
              placeholder="Search"
              onChange={(e) => {
                setKeyword(e.target.value);
                searchOnMenus(e.target.value);
              }}
            />
          </div>
        )}
        <div className={classNames("w-full flex-shrink items-center space-y-2 py-2 xl:flex xl:space-y-0 xl:px-2")}>
          <div
            ref={menuContainerRef}
            className={classNames("space-y-2 px-2 xl:flex xl:w-full xl:flex-wrap xl:gap-1 xl:space-y-0")}>
            {simulatedMenus.map((item, index) => {
              const isOverflow = overflowMenus.some((menu) => menu._id === item._id);
              if (isOverflow) return null;
              return item.type === "group" ? (
                <div
                  key={item._id}
                  ref={(el) => (menuItemsRef.current[index] = el)}>
                  <TopMenuItemGroup
                    data={item}
                    group_id={group_id}
                    page_id={page_id || form_id || document_id}
                    searchTerm={keyword ? keyword.toLowerCase() : ""}
                    toggleId={toggleId}
                    setToggleId={setToggleId}
                    handleClick={handleClick}
                  />
                </div>
              ) : (
                <div
                  key={item._id}
                  ref={(el) => (menuItemsRef.current[index] = el)}>
                  <TopMenuItemLink
                    data={item}
                    group_id={group_id}
                    image={item?.image}
                    page_id={page_id || form_id || document_id}
                    searchTerm={keyword ? keyword.toLowerCase() : ""}
                    handleClick={handleClick}
                  />
                </div>
              );
            })}

            {overflowMenus.length > 0 && (
              <div className="relative ml-1">
                <button
                  ref={moreButtonRef}
                  className={classNames(
                    "flex min-h-10 w-full items-center justify-between rounded px-4 py-2 font-medium transition-all  hover:bg-leftNavActiveColor hover:text-leftNavActiveTextColor",
                    showMoreDropdown || hasActiveInOverflow ? "bg-leftNavActiveColor text-leftNavActiveTextColor" : "text-leftNavTextColor",
                  )}
                  onClick={toggleMoreDropdown}>
                  More
                  <ChevronDownIcon className="ml-1 h-4 w-4" />
                </button>

                {showMoreDropdown && (
                  <div
                    ref={moreDropdownRef}
                    className="absolute right-0 z-50 mt-1 w-56 origin-top-right rounded-md bg-leftNavColor shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      {overflowMenus.map((item) => (
                        <div
                          key={item._id}
                          className="px-2 py-1">
                          {item.type === "group" ? (
                            <TopMenuItemGroup
                              data={item}
                              group_id={group_id}
                              page_id={page_id || form_id || document_id}
                              searchTerm={keyword ? keyword.toLowerCase() : ""}
                              toggleId={toggleId}
                              setToggleId={setToggleId}
                              dropdownPosition="right"
                              handleClick={(id) => {
                                handleClick(id);
                                setShowMoreDropdown(false);
                              }}
                            />
                          ) : (
                            <TopMenuItemLink
                              data={item}
                              group_id={group_id}
                              image={item?.image}
                              page_id={page_id || form_id || document_id}
                              searchTerm={keyword ? keyword.toLowerCase() : ""}
                              handleClick={() => {
                                handleClick();
                                setShowMoreDropdown(false);
                              }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TopNavigationMenu;
